"use client"

import Link from "next/link"
import { CardImage } from "./card-image"
import { alternateCardImage } from "@/lib/card-image"
import { NoImageFallback } from "@/components/ui/no-image-fallback"
import { StarRating } from "./star-rating"
import { BadgeList } from "@/components/badges/badge-list"
import type { Badge as BadgeData } from "@/components/badges/select"
import { dealCtaLabel, hasRenderedPrice } from "@/lib/deal-cta"
import { finFromProductHref, productCardCtaName, productCardLinkName, productCardQualifier } from "@/lib/card-link-names"
import { ArrowUpRightIcon } from "./icon-sprite"
// FRIDGE-874 — the heart and the compare tick. This is the card every collection,
// brand, filter, search and article-sidebar grid renders, and until now none of
// them had either control: "Saved" could never be populated by anyone shopping.
import { SaveHeartButton, CompareToggle } from "@/components/chrome/save-compare-controls"
import { buildSavedItem } from "@/lib/saved-compare-item"

// The shared visual treatment for the product detail CTA.
const CTA_CLASS =
  "relative z-10 mt-auto w-full bg-[#ff9951] hover:bg-[#ff8533] text-[var(--color-brand-action-text)] text-sm font-bold py-2.5 rounded-lg transition-all active:scale-95 flex items-center justify-center gap-2"

/**
 * The grid only needs the name-and-code form of the shared filter fallback.
 * Keeping that small treatment local prevents the shared product-card graph
 * from pulling the complete filter-card family into every product-grid route.
 */
function FilterGridFallback({ brand, code }: { brand?: string; code?: string | null }) {
  return (
    <div className="filter-brand-tile" data-filter-brand-tile="name" aria-hidden="true">
      <span className="filter-brand-tile__name">{brand || "Water filter"}</span>
      {code ? <span className="filter-brand-tile__code">{code}</span> : null}
    </div>
  )
}

function isLegacyFilterPlaceholder(src: string): boolean {
  return /(fallback-blog-hero|Fridge\.com-blue|Fridge\.com-logo|\/placeholder\.svg(?:[?#]|$))/i.test(src)
}

interface ProductGridCardProps {
  /** Product image URLs; lead and first distinct alternate are shown */
  images: string[]
  /** Brand name displayed as uppercase label above title */
  brand?: string
  /** Product title (already formatted) */
  title: string
  /** Formatted current price string (e.g. "$1,299") */
  price?: string
  /** Formatted original/average price for strikethrough (e.g. "$1,499") */
  originalPrice?: string
  /** Star rating (0–5) */
  rating?: number
  /** Product detail page URL */
  href: string
  /** Alt text for images (defaults to title) */
  alt?: string
  /** Whether to prioritize first image loading (for above-fold cards) */
  priority?: boolean
  /** Additional classes on the outer wrapper */
  className?: string
  /**
   * Canonical badges for this product (see docs/Page-Types-And-Templates.md).
   * When provided, rendered as a top-left overlay via the shared <BadgeList>.
   * Card surface → variant "card" (max 2). Pass the SAME array the product
   * shows on every other surface so badges persist consistently.
   */
  badges?: BadgeData[]
  /** Offer metadata remains accepted for existing callers; the card CTA opens the PDP (FRIDGE-814). */
  offerId?: string | null
  productId?: string | null
  retailerName?: string | null
  /**
   * FRIDGE-796 — what tells this row apart from another with the SAME title:
   * its model, or its minted number. Optional: appliance hrefs already encode a
   * FIN, so only callers whose URLs carry no number need to pass it. Never
   * rendered; it only reaches the links' accessible names.
   */
  identifier?: string | null
  /**
   * The (FFN) Fridge.com Filter Number of a filter row, or the (FPN) Fridge.com
   * Part Number of a part row, drawn as the one-class chip under the title —
   * the same chip the carousel card draws (FRIDGE-1088). Real-or-Hidden: only a
   * well-formed FFN or FPN renders. Appliance rows pass nothing.
   */
  familyNumber?: string | null
  /**
   * FRIDGE-1279 (owner, on a collection card: "FIN model #") — the (FIN)
   * Fridge.com Identification Number of an appliance row, drawn as a chip
   * between the title and the price, then its manufacturer model. Real-or-
   * Hidden: only a well-formed FIN renders; a row with no model shows the FIN
   * alone, never an empty label. Filter and part rows use `familyNumber`.
   */
  fin?: string | null
  /** The manufacturer model number shown after the FIN (FRIDGE-1279). */
  model?: string | null
}

/** A displayable model number: trimmed, never the FIN itself, never a placeholder. */
function cardModel(model: string | null | undefined, fin: string): string | null {
  const m = typeof model === 'string' ? model.trim() : ''
  if (!m || m.toUpperCase() === fin || /^(n\/?a|none|unknown|null|-+)$/i.test(m)) return null
  return m
}

/**
 * ProductGridCard — The standard product card used in hub page grids.
 *
 * Hover image, brand label, title,
 * price with optional strikethrough, star rating, and orange CTA button.
 * Used in 2×2, 3-col, and 4-col grids across category, brand, feature,
 * and specialty hub pages.
 */
export function ProductGridCard({
  images: sourceImages,
  brand,
  title,
  price,
  originalPrice,
  rating,
  href,
  alt,
  priority = false,
  className = "",
  badges,
  productId,
  identifier,
  familyNumber,
  fin,
  model,
}: ProductGridCardProps) {
  const isFilterCard = href.startsWith('/filters/')
  // A placeholder supplied by an older caller is still an absent filter photo.
  const images = isFilterCard ? sourceImages.filter(src => src.trim() && !isLegacyFilterPlaceholder(src)) : sourceImages

  // FRIDGE-796 — the image link's name carries the FIN, so two rows sharing one
  // manufacturer title cannot give one accessible name two URLs (WCAG 2.4.4).
  const altBase = (alt || title).endsWith(" | Fridge.com") ? (alt || title).slice(0, -" | Fridge.com".length) : (alt || title)
  const displayAlt = productCardLinkName(altBase, href, " | Fridge.com", identifier)
  const cardQualifier = productCardQualifier(href, identifier)
  // FRIDGE-1279 — the caller's FIN, else the one an appliance URL encodes
  // (`/products/<slug>-fin0000161`), so every list page's appliance card shows it.
  const cardFin = fin ?? (href.startsWith('/products/') ? finFromProductHref(href) : null)

  // FRIDGE-429 — ONE boolean gates the price row AND picks the CTA label, so
  // the button can never promise a deal above an empty price row. It replaces
  // the old inline `price && price !== "$0"` check and is a strict superset of
  // it: "$0.00" and "0" are zero too, and now hide like "$0" always did.
  const showsPrice = hasRenderedPrice(price)
  const ctaLabel = dealCtaLabel(showsPrice)

  // FRIDGE-874 — the row the Saved/Compare store keys this card by. `productId`
  // when the caller has it, the canonical path otherwise; the store matches on
  // either, so this card and the homepage rail cannot save one product twice.
  const savedItem = buildSavedItem({ id: productId, fin: identifier, brand, title, image: images[0], href, price })

  // The label reflects the displayed price.
  const ctaContent = (
    <>
      {ctaLabel}
      <ArrowUpRightIcon className="w-4 h-4" />
    </>
  )

  return (
    <div
      // FRIDGE-766: the one stable hook the crawler-text guard counts cards
      // by (lib/crawler-text/guard.ts checkCollectionPage). Class strings move
      // with the design; this attribute does not.
      data-card="product"
      className={`group relative bg-white rounded-xl border border-gray-100 hover:border-[#0469f0]/20 hover:translate-y-[-6px] hover:shadow-xl hover:shadow-black/5 transition-all duration-500 overflow-hidden flex flex-col h-full ${className}`}
    >
      {/* Save (heart) — z-20 puts it above BOTH the image block (z-10) and the
          info block's stretched ::after link overlay, so the tap saves rather
          than navigating. Mirrors the homepage card's top-right heart. */}
      <div className="absolute top-1 right-1 z-20">
        <SaveHeartButton item={savedItem} />
      </div>

      {/* Keep the image link above the card-wide title link. */}
      <div className="relative z-10">
        <BadgeList badges={badges} variant="card" />
        <div className="aspect-square bg-white overflow-hidden">
          {/* The photo-less link is named by its hidden text alone; an aria-label
              on top of it failed axe-core 4.14.0's label-content-name-mismatch
              (the fallback's visible brand and code are not a substring of the
              full label) on every brand page with a photo-less filter card. */}
          {images.length === 0 ? (
            <Link href={href} prefetch={false} className="block h-full">
              {isFilterCard ? <FilterGridFallback brand={brand} code={identifier} /> : <NoImageFallback />}
              <span className="sr-only">{displayAlt}</span>
            </Link>
          ) : (
            <CardImage src={images[0]} fallback={isFilterCard ? <FilterGridFallback brand={brand} code={identifier} /> : undefined} alternate={alternateCardImage(images[0], images)} href={href} alt={displayAlt} width={300} height={300} priority={priority} />
          )}
        </div>
      </div>

      {/* Product info */}
      <div className="p-4 border-t border-gray-50 flex flex-col flex-grow">
        {/* Primary card link — the stretched ::after overlay turns the WHOLE
            card surface (brand, title, price, rating, gaps) into a single
            navigable unit to the PDP. Keyboard-focusable; Enter navigates.
            No nested-anchor invalidity: the overlay is a pseudo-element. */}
        <Link
          href={href}
          prefetch={false}
          className="after:absolute after:inset-0 after:content-['']"
        >
          {/* FRIDGE-796 — this link's visible text (brand + title) is already
              its accessible name, so the disambiguator is appended as hidden
              text rather than restated in an aria-label that would repeat the
              whole title a third time in the document. Measured on
              /brands/lg 09/05/2026: 31 of these, ~100 bytes each as a label
              against 43 as a span. */}
          {cardQualifier && <span className="sr-only"> — {cardQualifier}</span>}
          {brand && (
            <span className="text-[10px] font-bold uppercase tracking-widest text-[#0469f0] mb-1 block">
              {brand}
            </span>
          )}
          {/* FRIDGE-325 — card title is a link label, not a document section.
              It already sits INSIDE the card's <Link>, so the text is the
              link's accessible name; as an <h3> it also became a heading, and a
              48-product grid page shipped 48 same-level headings that describe
              no sections. Classes carried over verbatim — semantics only. */}
          <p className="text-sm font-semibold text-gray-900 leading-snug mb-2 line-clamp-3 min-h-[3.6rem] group-hover:text-[#0469f0] transition-colors">
            {title}
          </p>
        </Link>
        {/* The FFN or FPN chip: inline and one class (app/globals.css .ffn-chip),
            like the carousel card, so this card pulls no identifier module for
            a slot only filter and part rows fill. */}
        {familyNumber && /^(FFN|FPN)-\d{7}$/.test(familyNumber) ? (
          <div className="-mt-1 mb-2">
            <span className="ffn-chip">{familyNumber}</span>
          </div>
        ) : cardFin && /^FIN-\d{7}$/.test(cardFin) ? (
          // FRIDGE-1279 — FIN first, then the model: the identifier row every
          // appliance card carries. The model wraps rather than truncates.
          <div className="-mt-1 mb-2 flex flex-wrap items-center gap-x-2 gap-y-1" data-card-ids>
            <span className="ffn-chip">{cardFin}</span>
            {cardModel(model, cardFin) ? <span className="text-[11px] font-medium text-gray-600 break-all">Model {cardModel(model, cardFin)}</span> : null}
          </div>
        ) : null}
        {/* FRIDGE-1507 P1: the row wraps, so a struck-through price that does
            not fit beside the price drops below it instead of being cut at
            the card's edge (a two-up card at 320 px leaves 100 to 106 px). */}
        {showsPrice && (
          <div className="flex flex-wrap items-baseline gap-x-2 mb-1">
            <span className="text-lg font-black text-gray-900">{price}</span>
            {/* FRIDGE-141: gray-600, not gray-400 — the struck price measured
                2.60:1 on white; the discount framing has to be readable. */}
            {hasRenderedPrice(originalPrice) && (
              <span className="text-xs text-gray-600 line-through">{originalPrice}</span>
            )}
          </div>
        )}
        {typeof rating === "number" && (
          <div className="mb-3">
            <StarRating rating={rating} />
          </div>
        )}
        {/* FRIDGE-814: inspect the product before choosing a retailer offer. */}
        <Link href={href} prefetch={false} aria-label={productCardCtaName(ctaLabel, { brand, title, identifier }, href)} className={CTA_CLASS}>
          {ctaContent}
        </Link>
        {/* Compare — `relative z-10` lifts it off the stretched link overlay so
            the tick toggles instead of navigating to the product page. */}
        <div className="relative z-10 mt-2">
          <CompareToggle item={savedItem} />
        </div>
      </div>
    </div>
  )
}
